// Route guard built on Auth0's withAuthenticationRequired HOC.
// Triggers the hosted-login redirect when the user is unauthenticated and optionally
// checks a permission set against the access-token claims (mirrors Angular's
// AuthGuard + ngxPermissions data.permissions pattern).
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { SplashScreen } from '@/components/SplashScreen';
import { usePermissions } from './usePermissions';

interface Props {
  children: ReactNode;
  permissions?: string[];
}

function PermissionGate({ children, permissions }: Props) {
  const { hasAny, isLoading } = usePermissions();
  if (isLoading) return <SplashScreen />;
  if (permissions && !hasAny(permissions)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

const AuthenticatedGate = withAuthenticationRequired(
  ({ children, permissions }: Props) => (
    <PermissionGate permissions={permissions}>{children}</PermissionGate>
  ),
  {
    onRedirecting: () => <SplashScreen />,
  },
);

export function RequireAuth({ children, permissions }: Props) {
  return <AuthenticatedGate permissions={permissions}>{children}</AuthenticatedGate>;
}
